/**
 * AdminRbmsPage — Rocky Backend Management System landing page.
 *
 * Route: `/admin-rbms`
 *
 * Gradient hero + entry cards (Access Control / Edit Workspace Homepage / CPA)
 * that route into the individual backend admin surfaces.
 * Gated on ADMIN_PAGE_LDAP_GROUP.
 *
 * Figma: Rocky-Assistant "Rocky 多Agent+后台" — BMS landing (node 3706:45723).
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BreadcrumbGroup from '@amzn/awsui-components-react/polaris/breadcrumb-group';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import { useAuth } from '../../context/AuthContext';
import { ADMIN_PAGE_LDAP_GROUP, appConfig } from '../../config';
import SafeTrans from '../../i18n/SafeTrans';
import './AdminRbmsPage.css';

/** Title and description come from settings:rbms.<key>Title / <key>Description. */
interface EntryCard {
  id: string;
  key: 'access' | 'homepage' | 'cpa';
  icon: 'grid-view' | 'list-view' | 'edit';
  tone: 'lime' | 'blue' | 'sand';
  to: string;
  /**
   * Stages this card is shown in. Omitted = every stage.
   *
   * Gates the ENTRY ONLY. The surface it links to is deliberately not
   * stage-gated: a card that is hidden in prod but whose page still answers is
   * the intended shape here — the page is unfinished, not secret, and anyone
   * holding the URL (or the admin group) may open it.
   *
   * Note `'dev'`: `resolveConfig()` maps localhost / 127.0.0.1 /
   * dev.rocky.ai.aws.dev to the `localhost` entry, whose stage is `'dev'`, NOT
   * `'beta'`. A list of just `['beta']` therefore hides the card on every local
   * dev server — which is the one place it most needs to be visible.
   */
  stages?: readonly string[];
}

const CARDS: EntryCard[] = [
  {
    id: 'access-control',
    key: 'access',
    icon: 'grid-view',
    tone: 'lime',
    to: '/admin/access-control',
  },
  {
    id: 'edit-workspace-homepage',
    key: 'homepage',
    icon: 'list-view',
    tone: 'blue',
    to: '/admin-rbms/edit-homepage',
  },
  {
    // Credit Program Advisor rule management. The Figma mock labels this tile and
    // its breadcrumb "CAP"; the product is Credit Program Advisor, so CPA is the
    // correct spelling and the mock's is a typo (confirmed 2026-10-29).
    id: 'cpa',
    key: 'cpa',
    // Both match the mock, which reuses Access Control's tile for this card.
    // ⚠ Open question for design: three tiles side by side with two of them
    // identical in both colour and glyph leaves nothing to tell them apart. An
    // unused `sand` tone already exists in the stylesheet if they want to
    // differentiate — until then this follows the design as drawn.
    icon: 'grid-view',
    tone: 'lime',
    to: '/admin-rbms/cpa',
    stages: ['dev', 'beta'],
  },
];

export default function AdminRbmsPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { ldap_groups } = useAuth();
  const cards = CARDS.filter((c) => !c.stages || c.stages.includes(appConfig.stage));

  if (!ldap_groups.includes(ADMIN_PAGE_LDAP_GROUP)) {
    return (
      <div className="rbms-denied">
        <h2>{t('rbms.deniedTitle')}</h2>
        <p>
          <SafeTrans t={t} i18nKey="rbms.deniedBody" values={{ group: ADMIN_PAGE_LDAP_GROUP }} components={{ code: <code /> }} />
        </p>
      </div>
    );
  }

  return (
    <div className="rbms-page">
      <div className="rbms-hero">
        <div className="rbms-hero__breadcrumb">
          <BreadcrumbGroup
            items={[
              { text: t('access.breadcrumbHome'), href: '/' },
              { text: t('access.breadcrumbRbms'), href: '/admin-rbms' },
            ]}
            onFollow={(e) => {
              e.preventDefault();
              navigate(e.detail.href);
            }}
          />
        </div>
        <h1 className="rbms-hero__title">{t('rbms.title')}</h1>
        <p className="rbms-hero__subtitle">
          {t('rbms.subtitle')}
        </p>
      </div>

      <div className="rbms-cards">
        {cards.map((card) => (
          <button
            key={card.id}
            type="button"
            className="rbms-card"
            onClick={() => navigate(card.to)}
          >
            <span className={`rbms-card__icon rbms-card__icon--${card.tone}`}>
              <Icon name={card.icon} size="medium" />
            </span>
            <span className="rbms-card__title">{t(`rbms.${card.key}Title`)}</span>
            <span className="rbms-card__desc">{t(`rbms.${card.key}Description`)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
